<script lang="ts">
	import classNames from 'classnames';
	import { onMount } from 'svelte';
	import { NewsPageBody } from '../../components/NewsPageBody';
	import { NewNewsCard } from '#lib/features/news/components/NewNewsCard/index.js';
	import { NewsletterWidget } from '#lib/features/news/components/NewsDataChart/NewsletterWidget/index.js';
	import { NewsMixpanelEvents } from '#lib/features/news/constants/mixpanel-events.js';
	import { useScrollDepthTracking } from '#lib/features/news/runes/useScrollDepthTracking/useScrollDepthTracking.svelte.js';
	import {
		ArticleContentType,
		getArticleMetadata,
	} from '#lib/features/news/utils/getArticleMetadata/getArticleMetadata.js';
	import { sanitizeArticleHref } from '#lib/features/news/utils/sanitizeArticleHref/sanitizeArticleHref.js';
	import { trackArticleRead } from '#lib/features/news/utils/trackArticleRead/trackArticleRead.js';
	import { page } from '$app/state';
	import { Container } from '$global/components/Container';
	import { Txt } from '$global/components/Txt';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import { track } from '$global/utils/track';
	import type { NewsViewPDPPageData } from '$routes/news/view/[id]/types';

	let rawArticle = $derived(
		page.data.article as NewsViewPDPPageData['article']
	);
	let article = $derived({
		...rawArticle,
		title: rawArticle.title ?? '',
		description: rawArticle.description ?? '',
		image:
			rawArticle.image && typeof rawArticle.image === 'object'
				? (getImageUrl(rawArticle.image.filename_disk) ?? '')
				: '',
		categories: (rawArticle.categories
			?.filter((cat) => cat)
			.map((cat) => cat?.HENI_News_Categories_id?.name)
			.filter(Boolean) ?? []) as string[],
		artists: rawArticle.artists ?? undefined,
	});
	let relatedArticles = $derived(
		page.data.relatedArticles as NewsViewPDPPageData['relatedArticles']
	);

	// The global category is the broadest topic, so it leads the topic list.
	let articleMetadata = $derived(
		getArticleMetadata({
			contentType: ArticleContentType.ExternalArticle,
			artists: rawArticle.artists?.map(
				(entry) => entry?.HENI_News_Artists_id?.name
			),
			topics: [rawArticle.globalCategory, ...article.categories],
			publishDate: rawArticle.publishDate ?? rawArticle.date_created,
		})
	);

	const dataCyPrefix = 'news-view';

	const handleLoad = (e: Event) => {
		const evt = e as unknown as {
			target: { src: string; naturalWidth: number };
		};

		if (evt.target && !evt?.target?.naturalWidth) {
			evt.target.src = '/img-svelte/placeholder.jpg';
		}
	};

	onMount(() => {
		const platform = page.url.searchParams.get('platform');
		if (platform) {
			track(NewsMixpanelEvents.VisitFromSocial, {
				id: article.id,
				platform,
			});
		}
	});

	// The guard keeps the read event to one per article: the effect also reruns
	// when page.data recomputes the derived article without changing articles.
	let trackedArticleId: string | undefined;

	$effect(() => {
		if (article.id && article.id !== trackedArticleId) {
			trackedArticleId = article.id;

			trackArticleRead({
				articleId: article.id,
				section: 'view',
				metadata: articleMetadata,
			});
		}
	});

	// Separate from the read effect above: this one has to re-run (and tear the
	// old observer down) whenever the article changes, so it reads article.id
	// reactively instead of guarding on it.
	$effect(() =>
		useScrollDepthTracking({
			articleId: article.id,
			section: 'view',
		})
	);
</script>

<NewsPageBody class="bg-white sm:bg-gray-100">
	<Container dataCy={dataCyPrefix} class="px-0">
		<div class="mb-4 grid grid-cols-12 sm:mt-4 sm:gap-4 lg:mt-10">
			<div class="col-span-12 md:col-span-6 lg:col-span-8">
				<NewNewsCard
					item={article}
					priority
					sizes="(min-width: 64rem) 52rem, (min-width: 48rem) 50vw, 100vw"
					class="md:col-span-1 md:mt-0"
				/>
			</div>

			<div
				class={classNames(
					'col-span-12 flex flex-col gap-4 border-0 border-gray-200  bg-white p-4 pb-0 sm:rounded sm:border sm:pb-6 md:col-span-6 lg:col-span-4'
				)}
			>
				<Txt variant="h5">Related Articles</Txt>

				{#each relatedArticles as relatedArticle (relatedArticle?.id || relatedArticle?.url)}
					<a
						href={sanitizeArticleHref(relatedArticle?.url)}
						target="_blank"
						rel="noopener noreferrer"
						class="grid grid-cols-2 gap-3"
					>
						<img
							onload={handleLoad}
							class="aspect-video h-full w-full object-cover"
							alt={relatedArticle?.title}
							src={relatedArticle?.image?.filename_disk
								? getImageUrl(relatedArticle?.image?.filename_disk, '512px')
								: '/img-svelte/placeholder.jpg'}
						/>
						<div>
							<Txt variant="h6" class="mb-1 line-clamp-2">
								{relatedArticle?.title}
							</Txt>
							<Txt variant="body3" class="line-clamp-2">
								{relatedArticle?.description}
							</Txt>
						</div>
					</a>
				{/each}
			</div>
		</div>

		<div class="pt-2 sm:my-8 sm:px-0 sm:pt-0 sm:pb-4">
			<NewsletterWidget />
		</div>
	</Container>
</NewsPageBody>
